import { LinearGradient } from 'expo-linear-gradient';
import type { ComponentType } from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import { colors } from '../../theme/colors';

type IconSize = 'sm' | 'md' | 'lg';

interface BrandIconProps {
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  size?: IconSize;
  style?: ViewStyle;
}

const DIMS: Record<IconSize, { box: number; radius: number; glyph: number }> = {
  sm: { box: 42, radius: 12, glyph: 19 },
  md: { box: 52, radius: 14, glyph: 23 },
  lg: { box: 60, radius: 16, glyph: 27 },
};

/**
 * Branded icon "well": a teal gradient surface with a hairline ring, an inset
 * top sheen and a soft outer glow (web), with a luminous glyph. Replaces the
 * flat monoline-in-a-muted-square treatment that read generic across the page.
 */
export const BrandIcon = ({ icon: Icon, size = 'md', style }: BrandIconProps) => {
  const d = DIMS[size];
  return (
    <View
      style={[
        styles.well,
        { width: d.box, height: d.box, borderRadius: d.radius },
        glowStyle,
        style,
      ]}
    >
      <LinearGradient
        colors={colors.metrics.gradients.visitors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[StyleSheet.absoluteFill, { borderRadius: d.radius }]}
      />
      <Icon size={d.glyph} color={colors.primary[300]} strokeWidth={1.75} />
    </View>
  );
};

// Inset top highlight + soft teal halo (web only; native falls back to the
// gradient + ring, still clearly elevated). Colors come from tokens.
const glowStyle = Platform.select({
  web: {
    boxShadow: `inset 0 1px 0 ${colors.primary.subtle}, 0 8px 22px ${colors.primary.muted}`,
  } as unknown as ViewStyle,
  default: {},
});

const styles = StyleSheet.create({
  well: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary.border,
    backgroundColor: colors.surface.iconWell,
  },
});
